/* eslint-disable react/no-children-prop */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import List from "app/pages/.shared/List.jsx";
import Separator from "app/pages/.shared/Separator";
import { PAYMENT_METHODS, PAYMENT_OPTIONS, USABLE_CREDITS_STATUS } from "app/constants";
import QuotationItemsContainer from "app/pages/Booking/Quotation/PriceDetails/QuotationItemsContainer";
import PayNowContainer from "app/pages/.shared/PayNow/PayNowContainer";
import Total from "app/pages/.shared/PriceSummary/Total/Total";
import TotalNote from "app/pages/.shared/PriceSummary/TotalNote/TotalNote";
import { paymentTypesValues } from "app/utils/propTypes";
import ExtraPriceDetailsContainer from "app/pages/Booking/Quotation/PriceDetails/ExtraPriceDetails/ExtraPriceDetailsContainer";
import "./PriceDetails.scss";
import { sendTagOnTotalToPayZero } from "app/utils/analytics";
import FlexAsideButtonContainer from "app/pages/Booking/Quotation/FlexRate/FlexAsideButton";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Collapse from "rc-collapse";

const PriceDetails = ({
	total,
	hasChildren,
	included,
	notIncluded,
	isPagePayment,
	mensualite,
	coutCredit,
	partialPayment,
	paymentType,
	paymentMode,
	isBookingEligibleForDeposit,
	usableCouponsStatus,
}) => {
	const isCalculatingPrices =
		(usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS) &&
		isPagePayment;

	const is2xPayment = paymentMode === PAYMENT_OPTIONS["2x"];
	const is4xPayment = paymentMode === PAYMENT_OPTIONS["4x"];

	const totalWithCreditFees = is4xPayment ? total + coutCredit : total;
	useEffect(() => {
		if (total === 0) {
			sendTagOnTotalToPayZero();
		}
	}, [total]);

	return (
		<div className="price-detail">
			<div className="price-detail__list">
				<QuotationItemsContainer />
				<ExtraPriceDetailsContainer />

				<div className="price-detail__item" data-cy="price-detail-total">
					<Total
						total={totalWithCreditFees}
						isBookingEligibleForDeposit={isBookingEligibleForDeposit}
						isPagePayment={isPagePayment}
						paymentMode={paymentMode}
						isLoading={isCalculatingPrices}
						isPaymentSummaryLabel={true}
					/>
				</div>

				<FlexAsideButtonContainer />

				<TotalNote showChildrenNote={hasChildren} />

				{/*	label payment 4x */}
				{(paymentType === PAYMENT_METHODS.CASINO ||
					paymentType === PAYMENT_METHODS.PLEDG) &&
					is4xPayment &&
					isPagePayment &&
					!isCalculatingPrices && (
						<React.Fragment>
							<Separator design="middle" />
							<div className="price-detail__item price-detail__item--total">
								<div className="price-detail__label">
									<FormattedMessage id="my.bookings.pay.now.label" />
								</div>
								<div className="price-detail__value price-detail__value-with-deposit">
									<AmountContainer amount={mensualite} />
								</div>
							</div>
						</React.Fragment>
					)}

				{/*	label payment 2x sans deposit */}
				{!isBookingEligibleForDeposit &&
					is2xPayment &&
					isPagePayment &&
					!isCalculatingPrices && (
						<React.Fragment>
							<Separator design="middle" />
							<div className="price-detail__item price-detail__item--total">
								<div className="price-detail__label">
									<FormattedMessage id="my.bookings.pay.now.label" />
								</div>
								<div className="price-detail__value price-detail__value-with-deposit">
									<AmountContainer amount={partialPayment.payNowAmount} />
								</div>
							</div>
						</React.Fragment>
					)}

				{/*	label payment 2x avec deposit */}
				{isBookingEligibleForDeposit &&
					is2xPayment &&
					isPagePayment &&
					!isCalculatingPrices && (
						<React.Fragment>
							<Separator design="middle" />
							<div className="price-detail-with-deposit">
								<PayNowContainer
									isPaymentPage={isPagePayment}
									withDepositIcon={false}
									mensualite2Fois={partialPayment}
									isPaymentPriceDetails
								/>
							</div>
						</React.Fragment>
					)}

				{!isPagePayment && isBookingEligibleForDeposit && !isCalculatingPrices && (
					<React.Fragment>
						<PayNowContainer withDepositIcon />
						<Separator design="middle" />
					</React.Fragment>
				)}

				<div className="price-detail__footer">
					<Collapse
						className="price-detail__collapse"
						expandIcon={({ isActive }) => {
							return (
								<IconArrowDown
									style={{
										transform: `rotate(${isActive ? 180 : 0}deg)`,
									}}
								/>
							);
						}}
					>
						<Collapse.Panel
							headerClass="price-detail__collapse-header"
							header={
								<div className="price-detail__collapse-title">
									<FormattedMessage id="price.detail.included.show.more.label" />
								</div>
							}
						>
							<div className="included">
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.included.title" /> :
									</div>
									<List items={included} enableHTMLFormatting={true} />
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.not.included.title" /> :
									</div>
									<List items={notIncluded} enableHTMLFormatting={true} />
								</div>
							</div>
						</Collapse.Panel>
					</Collapse>
				</div>
			</div>
		</div>
	);
};

PriceDetails.propTypes = {
	total: PropTypes.number,
	isPagePayment: PropTypes.bool,
	paymentType: paymentTypesValues,
	hasChildren: PropTypes.bool,
	included: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	notIncluded: PropTypes.arrayOf(PropTypes.string),
	mensualite: PropTypes.number,
	coutCredit: PropTypes.number,
	partialPayment: PropTypes.shape({
		dueDate: PropTypes.number,
		payNowAmount: PropTypes.number,
		payLaterAmount: PropTypes.number,
	}),
	paymentMode: PropTypes.number,
	usableCouponsStatus: PropTypes.string,
	isBookingEligibleForDeposit: PropTypes.bool,
};

export default PriceDetails;
